<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useRoute, useRouter } from 'vue-router'
import type { RecordedCrashDto } from '@roadfootage/api-client'
import { useApi } from '@/composables/api'
import { useIncidentSubmissionStore } from '@/stores/incident-submission'
import { useFileUploadStore } from '@/stores/file-upload'
import { useUserStore } from '@/stores/user/user.store'
import {
  crashSeverityLine,
  crashSourceAndDate,
  crashTypeAndAddress
} from '@/utils/recorded-crash-text'
import { formatNumber } from '@/utils/format'
import CrashStaticMap from '@/components/crash/CrashStaticMap.vue'

const route = useRoute()
const router = useRouter()
// Silent: a failed lookup shows the not-found state instead of a toast.
const { crashesApi } = useApi({ notifyOnError: false })
const incidentStore = useIncidentSubmissionStore()
const fileUploadStore = useFileUploadStore()
const { isRestricted } = storeToRefs(useUserStore())

const loading = ref(true)
const crash = ref<RecordedCrashDto | null>(null)

const hasLocation = computed(
  () => crash.value?.latitude !== undefined && crash.value?.longitude !== undefined
)

const footageLine = computed(() => {
  const count = crash.value?.footageCount ?? 0
  if (!crash.value?.hasFootage || count < 1) return 'No footage yet'
  return `${formatNumber(count)} ${count === 1 ? 'report has' : 'reports have'} footage of this crash`
})

// A route change to another crash id reuses this component, so reload on the id.
let latestRequest = 0
async function load(id: string) {
  const request = ++latestRequest
  loading.value = true
  crash.value = null
  let found: RecordedCrashDto | null = null
  try {
    found = await crashesApi.crashesGetById({ id })
  } catch (error) {
    console.error('Failed to load recorded crash:', error)
  }
  if (request !== latestRequest) return
  crash.value = found
  loading.value = false
}

watch(() => route.params.id as string, load, { immediate: true })

async function startUpload() {
  if (!crash.value) return
  incidentStore.$reset()
  fileUploadStore.$reset()
  incidentStore.prefillFromCrash(crash.value)
  await router.push({ name: 'upload' })
}
</script>

<template>
  <q-page padding class="bg-grey-2">
    <div class="layout-wide q-mx-auto">
      <q-skeleton v-if="loading" type="rect" height="320px" data-testid="crash-loading" />

      <q-banner
        v-else-if="!crash"
        rounded
        class="bg-white text-blue-grey-9"
        data-testid="crash-not-found"
      >
        <template #avatar>
          <q-icon name="mdi-map-marker-off" color="grey-7" />
        </template>
        <div class="text-h6">Crash not found</div>
        <div class="text-body2">
          We could not find this recorded crash. The link may be wrong, or the record is no longer
          available.
        </div>
        <template #action>
          <q-btn flat no-caps color="primary" label="Explore the map" :to="{ name: 'explore' }" />
        </template>
      </q-banner>

      <q-card v-else flat bordered data-testid="crash-details">
        <CrashStaticMap
          v-if="hasLocation"
          :latitude="crash.latitude as number"
          :longitude="crash.longitude as number"
        />
        <q-card-section>
          <div
            class="text-caption text-weight-bold text-uppercase text-amber-10"
            data-testid="crash-label"
          >
            Recorded crash
          </div>
          <h1 class="text-h5 text-weight-bold q-mt-xs q-mb-xs" data-testid="crash-source-date">
            {{ crashSourceAndDate(crash) }}
          </h1>
          <div class="text-body1" data-testid="crash-severity">{{ crashSeverityLine(crash) }}</div>
          <div class="text-body2 text-grey-8" data-testid="crash-type-address">
            {{ crashTypeAndAddress(crash) }}
          </div>
          <p class="text-caption text-grey-7 q-mt-sm q-mb-none">
            An official crash record, not footage.
          </p>
        </q-card-section>
        <q-separator />
        <q-card-section>
          <div class="text-subtitle1 text-weight-medium" data-testid="crash-footage">
            {{ footageLine }}
          </div>
          <q-btn
            v-if="!isRestricted"
            unelevated
            no-caps
            color="primary"
            icon="mdi-upload"
            class="q-mt-sm"
            label="Have footage of this? Upload it"
            data-testid="crash-upload"
            @click="startUpload"
          />
        </q-card-section>
      </q-card>
    </div>
  </q-page>
</template>
